<template>
  <toolbar-container>
    <template #toolbar>
      <reservations-toolbar
        :print-visible="false"
        :reservations="reservations"
        waitlist-popout
      >
        <v-select
          :value="tab"
          class="mr-5 mt-2"
          style="max-width: 200px"
          :items="tabs"
          hide-details
          :label="$i18n.t('label.section')"
          return-object
          dense
          @input="tabSelected"
        />
        <v-checkbox
          :input-value="showDetails"
          :label="$t('label.show_details')"
          hide-details
          dense
          @click.stop="setShowDetails(!showDetails)"
        />
      </reservations-toolbar>
    </template>

    <v-row class="ma-0 py-0">
      <v-col
        class="pa-0"
        cols="6"
        md="3"
      >
        <numeric-list-card
          :title="`${$t('label.guests')}/${$t('label.reservations')}`"
          :list-titles="[$t('label.section'), $t('label.guests'), 'Res.']"
          :list-values="guestsForSections"
          :count="`${dayStats.guests}/${dayStats.reservations}`"
          :no-values-label="$t('label.no_reservations')"
          :show-details="showDetails"
        />
      </v-col>
      <v-col
        class="pa-0"
        cols="6"
        md="3"
      >
        <numeric-list-card
          :title="$t('label.lunch_and_dinner_guests')"
          :list-titles="[$t('label.section'), $t('label.Lunch'), $t('label.Dinner')]"
          :list-values="lunchDinnerForSections"
          :count="`${dayStats.lunch}/${dayStats.dinner}`"
          :no-values-label="$t('label.no_reservations')"
          :show-details="showDetails"
        />
      </v-col>
      <v-col
        class="pa-0"
        cols="6"
        md="3"
      >
        <numeric-list-card
          :title="$t('label.labeled_guests_reservations')"
          :list-titles="[$t('label.label'), $t('label.guests'), $t('label.reservations_short')]"
          :list-values="guestsForLabels"
          :no-values-label="$t('label.no_labeled_reservations')"
          :count="`${dayStats.labeledGuests}/${dayStats.labeledReservations}`"
          :show-details="showDetails"
        />
      </v-col>
      <v-col
        class="pa-0"
        cols="6"
        md="3"
      >
        <numeric-list-card
          :title="$t('label.service_guests_reservations')"
          :list-titles="[$t('label.service'), $t('label.guests'), $t('label.reservations_short')]"
          :list-values="guestsForServices"
          :no-values-label="$t('label.no_service_reservations')"
          :count="`${dayStats.serviceGuests}/${dayStats.serviceReservations}`"
          :show-details="showDetails"
        />
      </v-col>
    </v-row>

    <v-row
      v-if="!availabilityHidden"
      class="ma-0 pa-0"
    >
      <v-col
        cols="12"
        class="pa-0"
      >
        <available-party-size-card
          :day-availability="dayAvailability"
          :day-limits="dayLimits"
          :disabled="availabilityDisabled"
          :tabs="availabilityTabs"
          @grid-clicked="makeReservation"
        />
        <v-divider />
      </v-col>
    </v-row>

    <v-row class="ma-0 pa-0">
      <v-col
        cols="12"
        class="pa-0"
      >
        <hourly-guests-card
          :dataset-seated="datasetSeated"
          :dataset-incoming="datasetIncoming"
          :dataset-incoming-limit="datasetIncomingLimit"
          :dataset-seated-limit="datasetSeatedLimit"
          :dataset-incoming-limit-visible="datasetIncomingLimitVisible"
          :dataset-seated-limit-visible="datasetSeatedLimitVisible"
        />
      </v-col>
    </v-row>
  </toolbar-container>
</template>

<script lang="ts">
import Vue from 'vue';

import ReservationsToolbar from '@/components/views/main/ReservationsToolbar.vue';
import HourlyGuestsCard from '@/components/views/dashboard/HourlyGuestsCard.vue';
import NumericListCard from '@/components/views/dashboard/NumericListCard.vue';
import AvailablePartySizeCard from '@/components/views/dashboard/AvailablePartySizeCard.vue';
import KeyboardControl from '@/mixins/keyboard-control';

import Tab from '@/model/Tab';
import {
  timeIndexFromSlot, dateFromDateAndTimeIndex,
  MORNING_TIME_SLOTS_COUNT, DAY_TIME_SLOTS_COUNT, dateIndexFromDate,
} from '@/services/time-utils';
import { performAction } from '@/services/vue-utils';
import Service from '@/model/Service';
import Label from '@/model/Label';
import Reservation from '@/model/Reservation';
import DashboardAvailability, { DashboardLimitData } from '@/grpc-api/model/dashboard-availability';
import { DashboardAllTabs, DashboardOnlineTabs } from '@/store/store';
import { tryEditReservation } from '@/services/reservation-edit-utils';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import DashboardStore from '@/store/dashboard-store';

export default Vue.extend({
  name: 'Dashboard',
  components: {
    ReservationsToolbar,
    HourlyGuestsCard,
    NumericListCard,
    AvailablePartySizeCard,
  },
  mixins: [KeyboardControl],

  data() {
    return {};
  },
  computed: {
    showDetails(): boolean {
      return this.$tstore.state.dashboard.showDetails ?? false;
    },
    date(): Date {
      return this.$tstore.state.update.date;
    },
    tab(): Tab {
      return this.$tstore.state.dashboard.tab;
    },
    tabs(): any[] {
      return [
        DashboardOnlineTabs, DashboardAllTabs,
        { divider: true },
        ...this.$tstore.state.settings.tabs,
      ];
    },
    reservations(): Reservation[] {
      return this.$tstore.getters.dashboardReservations;
    },
    // availability data
    dayAvailability(): DashboardAvailability | null {
      return this.$tstore.getters.dashboardDayAvailability;
    },
    dayLimits(): DashboardLimitData[] {
      const data = this.$tstore.getters.dashboardGuestsPerTime as DashboardLimitData[];
      return data;
    },
    availabilityHidden(): boolean {
      return !this.$tstore.getters.isTodayOrFuture || !this.$tstore.getters.dashboardIsOnlineTab;
    },
    availabilityDisabled(): boolean {
      return !this.$tstore.getters.isTodayOrFuture
      || !this.$tstore.getters.isDashboardDayAvailabilityLoaded
      || (this.tab.id > 0 && this.tab.usingWeekdaysAndTimes !== true);
    },
    availabilityTabs(): Tab[] {
      const { tabs } = this.$tstore.state.settings;
      if (this.tab === DashboardAllTabs) return tabs;
      if (this.tab === DashboardOnlineTabs) return tabs.filter((t: Tab) => t.usingWeekdaysAndTimes === true);
      return [this.tab];
    },
    // datasets
    guestsPerTime(): {t: Date, s: number, i: number, sl: number, il:number}[] {
      const data = this.$tstore.getters.dashboardGuestsPerTime as {i: number, s: number, il: number, sl: number}[];
      return data.map((sd, si) => {
        const di = dateIndexFromDate(this.date);
        const ti = timeIndexFromSlot(si);
        const date = dateFromDateAndTimeIndex(di, ti)!;
        return {
          t: date, ...sd,
        };
      }).slice(MORNING_TIME_SLOTS_COUNT, DAY_TIME_SLOTS_COUNT);
    },
    datasetSeated(): [Date, number][] {
      return this.guestsPerTime.map((sd) => ([sd.t, sd.s]));
    },
    datasetIncoming(): [Date, number][] {
      return this.guestsPerTime.map((sd) => ([sd.t, sd.i]));
    },
    datasetSeatedLimit(): [Date, number][] {
      return this.guestsPerTime.map((sd) => ([sd.t, sd.sl]));
    },
    datasetIncomingLimit(): [Date, number|undefined][] | undefined {
      return this.guestsPerTime.map((sd) => ([sd.t, Number.isFinite(sd.il) ? sd.il : undefined]));
    },
    datasetSeatedLimitVisible(): boolean {
      return this.tab === DashboardOnlineTabs || this.tab.usingWeekdaysAndTimes === true;
    },
    datasetIncomingLimitVisible(): boolean {
      return this.tab === DashboardOnlineTabs;
    },
    guestsForSections(): {text: String, guests: number, reservations: number}[] {
      const data = this.$tstore.getters.dashboardGuestsForSections as {s: Tab, g: number, r: number}[];
      return data.flatMap((sd) => (sd.r === 0 && sd.g === 0 ? []
        : { text: sd.s.name, guests: sd.g, reservations: sd.r }));
    },
    lunchDinnerForSections(): {text: String, guests: number, reservations: number}[] {
      const data = this.$tstore.getters.dashboardLunchDinnerForSections as {s: Tab, l: number, d: number}[];
      return data.flatMap((sd) => (sd.l === 0 && sd.d === 0 ? []
        : { text: sd.s.name, guests: sd.l, reservations: sd.d }));
    },
    guestsForServices(): {text: String, guests: number, reservations: number}[] {
      const data = this.$tstore.getters.dashboardGuestsForServices as {s: Service, g: number, r: number}[];
      return data.flatMap((sd) => (sd.r === 0 && sd.g === 0 ? []
        : { text: sd.s.text ?? 'N/A', guests: sd.g, reservations: sd.r }));
    },
    guestsForLabels(): {text: String, guests: number, reservations: number}[] {
      const data = this.$tstore.getters.dashboardGuestsForLabels.gr as {l: Label, g: number, r: number}[];
      return data.flatMap((ld) => (ld.r === 0 && ld.g === 0 ? []
        : { text: ld.l.text ?? 'N/A', guests: ld.g, reservations: ld.r }));
    },
    dayStats(): {
      reservations: number, tables: number, guests: number, lunch: number, dinner: number,
      labeledReservations: number, labeledGuests: number, serviceReservations: number, serviceGuests: number,
    } {
      return this.$tstore.getters.dashboardDayStats;
    },
  },
  watch: {
    date(date: Date) {
      this.setDate(date);
    },
  },
  created() {
    loadStoreModule(this, 'dashboard', DashboardStore, async () => {
      const { date } = this.$tstore.state.update;
      this.setDate(date);
    });
  },
  beforeDestroy() {
    unloadStoreModule(this, 'dashboard', async () => { this.$tstore.dispatch('resetDashboard'); });
  },
  methods: {

    async makeReservation(params: {timeSlot: number, partySize:number}) {
      const res = await tryEditReservation(this.$router, undefined, {
        tab: this.tab.id > 0 ? this.tab : undefined,
        tabItems: undefined,
        timeSlot: params.timeSlot + MORNING_TIME_SLOTS_COUNT,
        partySize: params.partySize,
      });
    },
    async setTab(tab: Tab) {
      await performAction(
        null,
        this.$i18n.tc('error.load_data'),
        async () => this.$tstore.dispatch('setDashboardTab', tab),
      );
    },
    async setDate(date: Date) {
      await performAction(
        null,
        this.$i18n.tc('error.load_data'),
        async () => this.$tstore.dispatch('setDashboardDate', date),
      );
    },
    tabSelected(tab: Tab) {
      this.setTab(tab);
    },
    setShowDetails(value: boolean) {
      this.$tstore.dispatch('setDashboardShowDetails', value);
    },
  },
});

</script>
